import { Link, createFileRoute, useRouter } from "@tanstack/react-router";
import { motion, useScroll, useTransform } from "framer-motion";
import {
	ArrowLeft,
	CalendarDays,
	Clock,
	Copy,
	HelpCircle,
	LinkIcon,
	Loader2,
	MapPin,
	Navigation,
	PartyPopper,
	Pencil,
	Share2,
	Undo2,
	Users,
	XCircle,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ErrorComponent } from "~/components/error";
import { CancelEventDialog } from "~/components/events/CancelEventDialog";
import { GuestList } from "~/components/events/GuestList";
import { HostInsights } from "~/components/events/HostInsights";
import { RsvpModal } from "~/components/events/RsvpModal";
import { MapPreview } from "~/components/events/map-preview";
import { Button } from "~/components/ui/button";
import { eventApi } from "~/lib/api/event";
import { useRsvp } from "~/lib/hooks/useRsvp";
import { useEventStore } from "~/lib/stores/event";
import { useUserStore } from "~/lib/stores/user";
import { cn } from "~/lib/utils";

export const Route = createFileRoute("/_protected/event/$eventSlug")({
	loader: async ({ params }) => {
		const response = await eventApi.getEvent(params.eventSlug);
		if (!response.success) {
			throw new Error(response.message);
		}
		return response.data;
	},
	component: () => <EventComponent />,
	errorComponent: ErrorComponent,
});

function EventComponent() {
	// when coming from invites site choice not working
	const choice = useEventStore((state) => state.choice);
	const clearChoice = useEventStore((state) => state.clearChoice);
	const { event, rsvps } = Route.useLoaderData();
	const locationRef = useRef<HTMLDivElement>(null);
	const guestListRef = useRef<HTMLDivElement>(null);
	const user = useUserStore((state) => state.user);
	const router = useRouter();
	const [isRsvpModalOpen, setIsRsvpModalOpen] = useState(false);
	const [isRestoring, setIsRestoring] = useState(false);
	const [clickedStatus, setClickedStatus] = useState<
		"accepted" | "maybe" | "declined" | null
	>(null);
	const { scrollY } = useScroll();
	const headerBackground = useTransform(
		scrollY,
		[0, 100],
		["rgba(0, 0, 0, 0)", "rgba(0, 0, 0, 0.9)"],
	);
	const headerShadow = useTransform(
		scrollY,
		[0, 100],
		[
			"0px 0px 0px rgba(255, 255, 255, 0)",
			"0px 8px 16px rgba(64, 98, 244, 0.1)",
		],
	);
	const { submitRsvp, isSubmitting } = useRsvp(event.id);

	// Format date and time
	const startDate = new Date(event.startDate);
	const formattedDate = new Intl.DateTimeFormat("en-US", {
		weekday: "long",
		month: "long",
		day: "numeric",
		year: "numeric",
	}).format(startDate);

	const formattedTime = new Intl.DateTimeFormat("en-US", {
		hour: "numeric",
		minute: "2-digit",
		hour12: true,
	}).format(startDate);

	// Calculate if event has passed
	const hasEventPassed = startDate < new Date();

	// Calculate days until event
	const now = new Date();
	const daysUntil = Math.ceil(
		(startDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24),
	);

	const container = {
		hidden: { opacity: 0 },
		show: {
			opacity: 1,
			transition: {
				staggerChildren: 0.1,
				duration: 0.2,
			},
		},
	};

	const item = {
		hidden: { opacity: 0, y: 10 },
		show: { opacity: 1, y: 0, transition: { duration: 0.2 } },
	};

	const isHost = user?.id === event.hostId;
	const userRsvp = rsvps.find((rsvp) => rsvp.userId === user?.id);

	const handleCopyLink = async () => {
		const inviteLink = `${window.location.origin}/invite/${event.slug}`;
		await navigator.clipboard.writeText(inviteLink);
		toast.success("Invite link copied!");
	};

	const handleShare = async () => {
		const inviteLink = `${window.location.origin}/invite/${event.slug}`;
		try {
			await navigator.share({
				title: event.title,
				text: `Join me at ${event.title}!`,
				url: inviteLink,
			});
		} catch (error) {
			// Fallback to copying the link
			await navigator.clipboard.writeText(inviteLink);
			toast.success("Link copied to clipboard!");
		}
	};

	const handleRsvp = useCallback(
		async (choice: "accepted" | "maybe" | "declined") => {
			setClickedStatus(choice);
			if (
				(event.allowPlusOne || event.collectDietaryPrefs) &&
				choice === "accepted"
			) {
				setIsRsvpModalOpen(true);
				setClickedStatus(null);
				return;
			}
			await submitRsvp({
				status: choice,
				bringingPlusOne: false,
			});

			const location = router.buildLocation({
				from: "/_protected/event/$eventSlug",
				to: "/_protected/event/$eventSlug",
				params: { eventSlug: event.slug },
				search: { choice: null },
			});

			await router.commitLocation(location);
			await router.invalidate();

			if (choice) {
				clearChoice();
			}
			setClickedStatus(null);
		},
		[
			event.allowPlusOne,
			event.collectDietaryPrefs,
			router,
			submitRsvp,
			clearChoice,
			event.slug,
		],
	);

	useEffect(() => {
		if (userRsvp?.status) {
			return;
		}
		if (choice && !hasEventPassed && !isSubmitting && !isHost) {
			handleRsvp(choice);
		}
	}, [
		handleRsvp,
		isSubmitting,
		choice,
		userRsvp?.status,
		hasEventPassed,
		isHost,
	]);

	// useEffect(() => {
	// 	console.log({ choice, clearChoice });
	// }, [choice, clearChoice]);

	return (
		<main className="min-h-screen bg-black text-primary-foreground">
			{/* Header */}
			<motion.header
				className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md"
				style={{
					backgroundColor: headerBackground,
					boxShadow: headerShadow,
				}}
			>
				<div className="max-w-3xl mx-auto px-4">
					<div className="h-16 flex items-center justify-between">
						<Link
							to="/"
							className="flex items-center gap-2 text-white/70 hover:text-white transition-colors"
						>
							<ArrowLeft className="w-5 h-5" />
							<span className="text-sm font-medium">Back</span>
						</Link>

						{!event.status.includes("cancelled") && (
							<div className="flex items-center gap-4 md:gap-6">
								<Button
									type="button"
									onClick={handleCopyLink}
									className="flex items-center gap-2 text-white/70 hover:text-white transition-colors hover:no-underline p-0"
									aria-label="Copy invite link"
									variant="link"
								>
									<Copy className="w-5 h-5" />
									<span className="text-sm font-medium">Copy</span>
								</Button>
								<Button
									type="button"
									onClick={handleShare}
									className="flex items-center gap-2 text-white/70 hover:text-white transition-colors hover:no-underline p-0"
									aria-label="Share event"
									variant="link"
								>
									<Share2 className="w-5 h-5" />
									<span className="text-sm font-medium">Share</span>
								</Button>
							</div>
						)}
					</div>
				</div>
			</motion.header>

			{/* Event Status Banner */}
			{hasEventPassed && (
				<motion.div
					initial={{ y: -100 }}
					animate={{ y: 0 }}
					transition={{ type: "spring", bounce: 0.2 }}
					className="fixed top-16 left-0 right-0 z-40 bg-primary/10 backdrop-blur-xl border-b border-white/10 py-2"
				>
					<div className="max-w-3xl mx-auto px-4">
						<div className="flex items-center justify-center gap-2 text-sm font-medium text-chart-1/50">
							<Clock className="w-4 h-4" strokeWidth={2.5} />
							<span>This event has already taken place</span>
						</div>
					</div>
				</motion.div>
			)}

			<div
				className={cn(
					"relative min-h-screen pt-16",
					hasEventPassed && "pt-24",
					!hasEventPassed && "mb-16",
				)}
			>
				{/* Background */}
				<div className="fixed inset-0 bg-gradient-to-b from-black from-85% to-primary/5" />

				{/* Content */}
				<div className="relative z-10 max-w-3xl mx-auto px-4 py-8 md:py-12">
					{/* Header Section with Image */}
					<div className="md:grid md:grid-cols-2 md:gap-8 mb-6">
						{/* Event Image */}
						{event.coverImage && (
							<motion.div
								variants={item}
								className="w-3/4 md:w-full mx-auto aspect-square overflow-hidden rounded-2xl border border-white/10 bg-primary/10 mb-6 md:mb-0"
							>
								<motion.img
									src={event.coverImage}
									alt={`${event.title} cover`}
									className="w-full h-full object-cover"
									whileHover={{ scale: 1.05 }}
									whileTap={{ scale: 1.02 }}
									transition={{ duration: 0.3, ease: "easeOut" }}
								/>
							</motion.div>
						)}

						{/* Title Section */}
						<motion.div
							variants={container}
							initial="hidden"
							animate="show"
							className={cn(
								"text-center md:text-left flex flex-col justify-center w-full",
								!event.coverImage &&
									"md:col-span-2 md:max-w-2xl md:mx-auto md:py-4",
							)}
						>
							{/* Event Badge */}
							<motion.div variants={item}>
								<div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 backdrop-blur-sm border border-white/10 mb-3 mt-4">
									{!hasEventPassed ? (
										<PartyPopper className="w-4 h-4 text-accent" />
									) : (
										<Clock className="w-4 h-4 text-accent" />
									)}
									<span className="text-sm font-medium text-primary-foreground">
										{daysUntil > 0
											? `${daysUntil} ${daysUntil === 1 ? "day" : "days"} to go!`
											: daysUntil === 0
												? "Event starts today!"
												: "Event has passed!"}
									</span>
								</div>
							</motion.div>

							{/* Title */}
							<motion.h1
								variants={item}
								className={cn(
									"font-bold mb-2 text-primary-foreground font-heading tracking-wide",
									event.coverImage
										? "text-4xl md:text-5xl"
										: "text-6xl md:text-8xl bg-gradient-to-br from-white via-white/90 to-primary-foreground/80 bg-clip-text text-transparent",
								)}
							>
								{event.title}
							</motion.h1>
							<motion.p
								variants={item}
								className={cn(
									"text-secondary",
									event.coverImage ? "text-lg" : "text-2xl tracking-wide",
								)}
							>
								Hosted by{" "}
								<Link
									to="/u/$profile"
									params={{ profile: event.username }}
									className="text-accent font-medium"
								>
									{event.hostName}
								</Link>
							</motion.p>
						</motion.div>
					</div>

					{/* Details Card */}
					<motion.div
						variants={container}
						initial="hidden"
						animate="show"
						className={cn(
							"space-y-6 backdrop-blur-xl bg-primary/10 rounded-3xl border border-white/10 p-6 md:p-8",
							!event.coverImage && "mt-8",
						)}
					>
						{/* Event Details */}

						{/* Date and Time */}
						<motion.div variants={item} className="flex items-start gap-4">
							<div className="shrink-0 p-3 rounded-xl bg-accent/10 border border-accent/20">
								<CalendarDays className="w-6 h-6 text-accent" />
							</div>
							<div>
								<h2 className="font-medium mb-1 text-primary-foreground">
									Date
								</h2>
								<p className="text-secondary">{formattedDate}</p>
							</div>
						</motion.div>

						<motion.div variants={item} className="flex items-start gap-4">
							<div className="shrink-0 p-3 rounded-xl bg-accent/10 border border-accent/20">
								<Clock className="w-6 h-6 text-accent" />
							</div>
							<div>
								<h2 className="font-medium mb-1 text-primary-foreground">
									Time
								</h2>
								<p className="text-secondary">{formattedTime}</p>
							</div>
						</motion.div>

						{/* Location */}
						{!event.locationCoordinates ? (
							<motion.div variants={item} className="flex items-start gap-4">
								<div className="shrink-0 p-3 rounded-xl bg-accent/10 border border-accent/20">
									<MapPin className="w-6 h-6 text-accent" />
								</div>
								<div>
									<h2 className="font-medium mb-1 text-primary-foreground">
										Location
									</h2>
									<p className="text-secondary">
										{event.locationAddress ||
											"Location will be shared after RSVP"}
									</p>
								</div>
							</motion.div>
						) : (
							<motion.div variants={item} className="flex items-start gap-4">
								<div className="shrink-0 p-3 rounded-xl bg-accent/10 border border-accent/20">
									<MapPin className="w-6 h-6 text-accent" />
								</div>
								<div>
									<h2 className="font-medium mb-1 text-primary-foreground">
										Location
									</h2>
									<p className="text-secondary">
										{event.locationAddress || "View map below"}
									</p>
									<button
										type="button"
										onClick={() => {
											locationRef.current?.scrollIntoView({
												behavior: "smooth",
											});
										}}
										className="text-accent-dark text-sm mt-1"
									>
										Map available below
									</button>
								</div>
							</motion.div>
						)}

						{/* Guest Count */}
						<motion.div variants={item} className="flex items-start gap-4">
							<div className="shrink-0 p-3 rounded-xl bg-accent/10 border border-accent/20">
								<Users className="w-6 h-6 text-accent" />
							</div>
							<button
								type="button"
								onClick={() => {
									guestListRef.current?.scrollIntoView({
										behavior: "smooth",
									});
								}}
								className="w-full text-left"
							>
								<h2 className="font-medium mb-1 text-primary-foreground">
									Guests
								</h2>
								<p className="text-secondary flex items-center flex-wrap space-x-3">
									<span>
										{userRsvp
											? `${rsvps.reduce((acc, rsvp) => {
													if (rsvp.status === "accepted") {
														if (rsvp.bringingPlusOne) {
															return acc + 2;
														}
														return acc + 1;
													}
													return acc;
												}, 0)} attending`
											: "Guest count will be visible after RSVP"}
									</span>
									{event.guestLimit && (
										<span className="px-2 py-0.5 rounded-full bg-accent-dark text-xs text-white">
											{event.guestLimit} max
										</span>
									)}
									{event.allowPlusOne && (
										<span className="px-2 py-0.5 rounded-full bg-accent/10 text-accent text-sm">
											Plus ones welcome
										</span>
									)}
								</p>
								{!isHost && !event.guestListVisibility && (
									<p className="text-muted-foreground text-sm mt-1">
										Guest list will be visible after RSVP
									</p>
								)}
							</button>
						</motion.div>

						{/* Description */}
						{event.description && (
							<motion.div variants={item} className="mt-8">
								<div className="p-6 rounded-2xl bg-primary/10 border border-white/10">
									<p className="text-secondary whitespace-pre-wrap leading-relaxed">
										{event.description}
									</p>
								</div>
							</motion.div>
						)}
					</motion.div>

					{/* Location Map Section - New dedicated section */}
					{event.locationCoordinates && (
						<motion.div
							variants={item}
							className="mt-8 backdrop-blur-xl bg-primary/10 rounded-3xl border border-white/10 overflow-hidden"
							ref={locationRef}
						>
							<div className="p-4 md:p-5 border-b border-white/20">
								<h3 className="font-medium text-xl text-primary-foreground flex items-center">
									<MapPin className="w-5 h-5 mr-2 text-accent" />
									Location Map
								</h3>
								{event.locationAddress && (
									<p className="text-secondary mt-1 pl-7">
										{event.locationAddress}
									</p>
								)}
							</div>
							<MapPreview
								coordinates={event.locationCoordinates}
								className="w-full h-[240px] md:h-[320px]"
							/>
							<div className="p-4 md:p-5 bg-black/40 flex flex-wrap items-center justify-between gap-4">
								<div className="flex gap-3">
									{event.locationUrl && (
										<a
											href={event.locationUrl}
											target="_blank"
											rel="noopener noreferrer"
											className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-accent/10 border border-accent/20 text-accent hover:bg-accent/20 transition-colors"
										>
											<LinkIcon className="h-4 w-4" />
											View on Maps
										</a>
									)}
									<a
										href={`https://www.google.com/maps/dir/?api=1&destination=${event.locationCoordinates}`}
										target="_blank"
										rel="noopener noreferrer"
										className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-accent/10 border border-accent/20 text-accent hover:bg-accent/20 transition-colors"
									>
										<Navigation className="h-4 w-4" />
										Get Directions
									</a>
								</div>
							</div>
						</motion.div>
					)}

					{/* Host Insights */}
					{isHost && (
						<motion.div variants={item} className="mt-8">
							<HostInsights event={event} rsvps={rsvps} />
						</motion.div>
					)}

					{/* Guest List */}
					{event.guestListVisibility || isHost ? (
						<motion.div variants={item} className="mt-8" ref={guestListRef}>
							<GuestList rsvps={rsvps} userRsvp={userRsvp} isHost={isHost} />
						</motion.div>
					) : null}
				</div>
			</div>

			{/* Fixed Bottom RSVP Bar */}
			{!isHost && !hasEventPassed && (
				<motion.div
					initial={{ y: 100 }}
					animate={{ y: 0 }}
					transition={{ type: "spring", bounce: 0.2 }}
					className="fixed bottom-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-xl border-t border-white/10 pb-safe"
				>
					<div className="max-w-3xl mx-auto px-4 py-4">
						<div className="grid grid-cols-3 gap-4">
							<Button
								size="lg"
								className={cn(
									"py-6 rounded-2xl font-semibold text-base",
									"bg-white/5 hover:bg-accent active:bg-accent-dark",
									"text-white/70 hover:text-accent-foreground",
									"transition-all duration-300",
									"border border-white/10 hover:border-accent",
									"shadow-[0_0_0_0] hover:shadow-[0_0_32px_rgba(111,136,247,0.3)]",
									userRsvp?.status === "accepted" &&
										"border-accent shadow-[0_0_32px_rgba(111,136,247,0.3)] bg-accent text-white/70",
								)}
								onClick={() => handleRsvp("accepted")}
								disabled={isSubmitting}
							>
								<span className="flex items-center gap-2">
									{isSubmitting && clickedStatus === "accepted" ? (
										<Loader2 className="size-5 text-white/70 animate-spin" />
									) : (
										<PartyPopper className="w-5 h-5" strokeWidth={2.5} />
									)}
									<span>Going!</span>
								</span>
							</Button>
							<Button
								size="lg"
								className={cn(
									"py-6 rounded-2xl font-semibold text-base",
									"bg-white/5 hover:bg-amber-500 active:bg-amber-500",
									"text-white/70 hover:text-white/70",
									"transition-all duration-300",
									"border border-white/10 hover:border-amber-600",
									"shadow-[0_0_0_0] hover:shadow-[0_0_32px_rgba(255,165,0,0.3)]",
									userRsvp?.status === "maybe" &&
										"border-amber-600 shadow-[0_0_32px_rgba(255,165,0,0.3)] bg-amber-500 text-white/70",
								)}
								onClick={() => handleRsvp("maybe")}
								disabled={isSubmitting}
							>
								<span className="flex items-center gap-2">
									{isSubmitting && clickedStatus === "maybe" ? (
										<Loader2 className="size-5 text-white/70 animate-spin" />
									) : (
										<HelpCircle className="w-5 h-5" strokeWidth={2.5} />
									)}
									<span>Maybe</span>
								</span>
							</Button>
							<Button
								size="lg"
								className={cn(
									"py-6 rounded-2xl font-semibold text-base",
									"bg-white/5 hover:bg-destructive active:bg-destructive-dark",
									"text-white/70 hover:text-white",
									"transition-all duration-300",
									"border border-white/10 hover:border-destructive",
									"shadow-[0_0_0_0] hover:shadow-[0_0_32px_rgba(238,57,57,0.3)]",
									userRsvp?.status === "declined" &&
										"border-destructive shadow-[0_0_32px_rgba(238,57,57,0.3)] bg-destructive text-white/70",
								)}
								onClick={() => handleRsvp("declined")}
								disabled={isSubmitting}
							>
								<span className="flex items-center gap-2">
									{isSubmitting && clickedStatus === "declined" ? (
										<Loader2 className="size-5 text-white/70 animate-spin" />
									) : (
										<XCircle className="w-5 h-5" strokeWidth={2.5} />
									)}
									<span>No</span>
								</span>
							</Button>
						</div>
					</div>
				</motion.div>
			)}

			{/* Host Controls Bar - Only show if event hasn't passed */}
			{isHost && !event.status.includes("cancelled") && !hasEventPassed && (
				<motion.div
					initial={{ y: 100 }}
					animate={{ y: 0 }}
					transition={{ type: "spring", bounce: 0.2 }}
					className="fixed bottom-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-xl border-t border-white/10 pb-safe"
				>
					<div className="max-w-3xl mx-auto px-4 py-4">
						<div className="flex items-center justify-between gap-4">
							<div className="items-center gap-3 hidden md:flex">
								<div className="p-2 rounded-full bg-accent/10">
									<PartyPopper className="w-5 h-5 text-accent" />
								</div>
								<div>
									<h3 className="font-medium text-primary-foreground">
										Host Controls
									</h3>
									<p className="text-sm text-primary-foreground/60">
										Manage your event
									</p>
								</div>
							</div>
							<div className="flex items-center gap-3">
								<Link
									to="/event/$eventSlug/edit"
									params={{ eventSlug: event.slug }}
									className={cn(
										"inline-flex items-center justify-center gap-2 h-12 px-6 rounded-xl",
										"text-accent border border-accent/20",
										"hover:bg-accent/20 hover:border-accent/30",
										"transition-all duration-300",
										"font-medium",
									)}
								>
									<Pencil className="w-4 h-4" />
									Edit Event
								</Link>
								<CancelEventDialog
									eventId={event.id}
									eventTitle={event.title}
									onCancelled={router.invalidate}
								>
									<Button
										variant="outline"
										className={cn(
											"flex items-center justify-center gap-2 h-12 px-6 rounded-xl",
											"bg-transparent text-destructive/70 border-destructive/20",
											"hover:bg-destructive/10 hover:text-destructive hover:border-destructive/30",
											"transition-all duration-300",
										)}
									>
										<XCircle className="w-4 h-4" />
										Cancel Event
									</Button>
								</CancelEventDialog>
							</div>
						</div>
					</div>
				</motion.div>
			)}

			{/* Restore Event Button for cancelled events */}
			{isHost && event.status.includes("cancelled") && (
				<motion.div
					initial={{ y: 100 }}
					animate={{ y: 0 }}
					transition={{ type: "spring", bounce: 0.2 }}
					className="fixed bottom-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-xl border-t border-white/10 pb-safe"
				>
					<div className="max-w-3xl mx-auto px-4 py-4">
						<Button
							size="lg"
							variant="outline"
							disabled={isRestoring}
							className={cn(
								"w-full py-3 rounded-xl",
								"bg-accent/10 text-accent border-accent/20",
								"hover:bg-accent/20 hover:text-accent hover:border-accent/40",
								"transition-all duration-300",
							)}
							onClick={async () => {
								try {
									setIsRestoring(true);
									await eventApi.restoreEvent(event.id);
									toast.success(
										<div className="flex items-center gap-3">
											<div className="p-2 rounded-full bg-accent/10">
												<PartyPopper className="w-5 h-5 text-accent" />
											</div>
											<div className="flex flex-col gap-0.5">
												<p className="font-medium">Event Restored!</p>
												<p className="text-sm text-muted-foreground">
													All guests will be notified
												</p>
											</div>
										</div>,
									);
									await router.invalidate();
								} catch (error) {
									toast.error(
										<div className="flex flex-col gap-1">
											<p className="font-medium">Failed to restore event</p>
											<p className="text-sm opacity-80">Please try again</p>
										</div>,
									);
								} finally {
									setIsRestoring(false);
								}
							}}
						>
							{isRestoring ? (
								<div className="flex items-center gap-2">
									<div className="w-5 h-5 border-2 border-accent border-t-transparent rounded-full animate-spin" />
									<span>Restoring...</span>
								</div>
							) : (
								<>
									<Undo2 className="w-5 h-5 mr-2" />
									Restore Event
								</>
							)}
						</Button>
					</div>
				</motion.div>
			)}

			{/* RSVP Modal */}
			<RsvpModal
				eventId={event.id}
				open={isRsvpModalOpen}
				onClose={() => setIsRsvpModalOpen(false)}
				allowPlusOne={event.allowPlusOne}
				collectDietaryPrefs={event.collectDietaryPrefs}
			/>
		</main>
	);
}
